import { useState } from "react";
import { apiErrorCode } from "@/api/client";
import * as endpoints from "@/api/endpoints";
import { Button, Card, Input } from "@/components/ui/primitives";
import { useAuth } from "@/hooks/use-auth";

/**
 * Two ways in, and the second one is not a convenience.
 *
 * The operator password lives in an environment variable on a server and is
 * typed perhaps once a quarter, so it is forgotten sooner or later — and when
 * it is, the panel is shut with no way back in short of editing the server and
 * restarting it. A code mailed to the operator address is a credential that
 * cannot be mislaid the same way.
 *
 * The password stays as the default: it is faster when you know it, and it
 * still works on a day when mail delivery does not.
 */
type Mode = "password" | "code";

export function LoginPage() {
  const { signIn, signInWithCode } = useAuth();
  const [mode, setMode] = useState<Mode>("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function describe(caught: unknown) {
    const status = apiErrorCode(caught);
    return status === "SYSTEM_UNCONFIGURED"
      ? "Sunucuda SYSTEM_ADMIN_EMAIL tanımlı değil"
      : status === "CODE_INVALID"
        ? "Kod geçersiz veya süresi dolmuş"
        : status === "VALIDATION_ERROR"
          ? "Kod altı haneli olmalı"
          : status
            ? "E-posta veya şifre hatalı"
            : "Sunucuya ulaşılamadı";
  }

  async function submitPassword(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email, password);
    } catch (caught) {
      setError(describe(caught));
    } finally {
      setBusy(false);
    }
  }

  async function sendCode() {
    setBusy(true);
    setError(null);
    try {
      await endpoints.requestLoginCode();
      // Says nothing about which address it went to. Anyone can reach this
      // button, and the mailbox that administers the system is not their
      // business.
      setSent(true);
    } catch (caught) {
      setError(describe(caught));
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signInWithCode(code.trim());
    } catch (caught) {
      setError(describe(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <Card className="w-full max-w-sm p-6">
        <div className="mb-6 text-center">
          <h1 className="text-xl font-bold tracking-tight">ContactPool</h1>
          <p className="text-sm text-muted-foreground">Sistem Paneli</p>
        </div>

        {mode === "password" ? (
          <form onSubmit={submitPassword} className="space-y-3">
            <div className="space-y-1">
              <label htmlFor="email" className="text-sm font-medium">E-posta</label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="password" className="text-sm font-medium">Şifre</label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button type="submit" className="w-full" disabled={busy}>Giriş yap</Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={() => {
                setMode("code");
                setError(null);
              }}
            >
              Şifremi bilmiyorum
            </Button>
          </form>
        ) : (
          <form onSubmit={submitCode} className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Sistem yöneticisi adresine altı haneli bir kod gönderilir. Kod on
              dakika geçerlidir ve bir kez kullanılabilir.
            </p>

            {!sent ? (
              <Button type="button" className="w-full" disabled={busy} onClick={sendCode}>
                {busy ? "Gönderiliyor…" : "Kod gönder"}
              </Button>
            ) : (
              <>
                <div className="space-y-1">
                  <label htmlFor="code" className="text-sm font-medium">Giriş kodu</label>
                  <Input
                    id="code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    required
                    value={code}
                    onChange={(event) =>
                      setCode(event.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    className="text-center text-lg tracking-[0.4em]"
                  />
                </div>
                <Button type="submit" className="w-full" disabled={busy || code.length < 6}>
                  Giriş yap
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  disabled={busy}
                  onClick={sendCode}
                >
                  Yeni kod gönder
                </Button>
              </>
            )}

            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={() => {
                setMode("password");
                setSent(false);
                setError(null);
              }}
            >
              Şifreyle giriş
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
}
